import { inject, Injectable } from '@angular/core';
import { CollectionRequestBuilder } from '@web/common';
import { TableDataPayload, TableLazyLoad } from '@web/shared/ui';
import { catchError, map, Observable, of } from 'rxjs';
import { Guid, valueOrDefault } from '@web/utils';
import { PublicBoxQuestionnaireApiService } from '../api/services';
import {
  PublicBoxQuestionnaireFillQuestionRequest,
  PublicBoxQuestionnaireFillRequest,
} from '../api/requests';
import {
  PublicBoxQuestionnaireCollectionFilter,
  PublicBoxQuestionnaireCollectionItem,
  PublicBoxQuestionnaireContextOperations,
  PublicBoxRecommendedQuestionnaire,
} from '../models';
import {
  BoxQuestionnaireDetails,
  BoxQuestionnaireQuestion,
  BoxQuestionnaireQuestionOption,
  BoxQuestionnaireQuestionOptionAttribute,
} from '../../shared/models';

@Injectable({
  providedIn: 'root',
})
export class PublicBoxQuestionnaireDataSourceService {
  /* prettier-ignore */
  private readonly boxQuestionnaireApi = inject(PublicBoxQuestionnaireApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);

  /* prettier-ignore */
  public fetchCollection(
    lazyLoad: TableLazyLoad<PublicBoxQuestionnaireCollectionFilter>,
  ): Observable<TableDataPayload<PublicBoxQuestionnaireCollectionItem>> {
    return this.boxQuestionnaireApi
      .fetchCollection({
        request: this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('name').build())
              .appendText((builder) => builder.inFor('status').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('orderNumber').build())
              .buildAsArray(),
          )
          .build(),
      })
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  new PublicBoxQuestionnaireCollectionItem({
                    createdAt: item.createdAt,
                    endDate: item.endDate,
                    id: item.id,
                    isShown: item.isShown,
                    logoAlt: item.logo.alternativeText,
                    logoUrl: item.logo.url,
                    name: item.name,
                    orderId: item.order.id,
                    orderNumber: item.order.number,
                    contextOperations: this.mapContextOperations(
                      item.contextOperations,
                    ),
                    status: item.status,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchNotShownCount(): Observable<number> {
    return this.boxQuestionnaireApi.fetchSummary().pipe(
      map(({ notShown }) => notShown),
      catchError(() => of(0)),
    );
  }

  public fetchDetails(id: Guid): Observable<BoxQuestionnaireDetails> {
    return this.boxQuestionnaireApi.fetchDetails(id).pipe(
      map(
        (response) =>
          new BoxQuestionnaireDetails({
            contextOperations: this.mapContextOperations(
              response.contextOperations,
            ),
            createdAt: response.createdAt,
            endDate: response.endDate,
            description: response.description,
            id: response.id,
            isShown: response.isShown,
            logoAlt: response.logo.alternativeText,
            logoUrl: response.logo.url,
            name: response.name,
            orderId: response.order.id,
            orderNumber: response.order.number,
            questions: response.questions.map(
              (question) =>
                new BoxQuestionnaireQuestion({
                  description: question.description,
                  id: question.id,
                  options: question.options.map(
                    (option) =>
                      new BoxQuestionnaireQuestionOption({
                        id: option.id,
                        imageAlt: option.featureFile?.alternativeText,
                        imageUrl: option.featureFile?.url,
                        isAvailable: option.isAvailable,
                        isAutoSelected: option.isAutoSelected,
                        isSelected: option.isSelected,
                        name: option.name,
                        sourceId: option.source?.id,
                        sourceName: option.source?.name,
                        attributes: valueOrDefault(option.attributes, []).map(
                          (attribute) =>
                            new BoxQuestionnaireQuestionOptionAttribute({
                              label: attribute.label,
                              id: attribute.id,
                              value: attribute.value,
                            }),
                        ),
                      }),
                  ),
                }),
            ),
            recommendedQuestionnaires: response.recommendedQuestionnaires.map(
              (item) =>
                new PublicBoxRecommendedQuestionnaire({
                  id: item.id,
                  name: item.name,
                  status: item.status,
                }),
            ),
            status: response.status,
          }),
      ),
    );
  }

  public fill(options: {
    id: Guid;
    questions: {
      id: Guid;
      selectedOptionId: Guid;
    }[];
  }): Observable<BoxQuestionnaireDetails> {
    const request = new PublicBoxQuestionnaireFillRequest({
      questions: options.questions.map(
        (question) => new PublicBoxQuestionnaireFillQuestionRequest(question),
      ),
    });

    return this.boxQuestionnaireApi
      .fill({
        id: options.id,
        request,
      })
      .pipe(
        map(
          (response) =>
            new BoxQuestionnaireDetails({
              contextOperations: this.mapContextOperations(
                response.contextOperations,
              ),
              createdAt: response.createdAt,
              endDate: response.endDate,
              description: response.description,
              id: response.id,
              isShown: response.isShown,
              logoAlt: response.logo.alternativeText,
              logoUrl: response.logo.url,
              name: response.name,
              orderId: response.order.id,
              orderNumber: response.order.number,
              questions: response.questions.map(
                (question) =>
                  new BoxQuestionnaireQuestion({
                    description: question.description,
                    id: question.id,
                    options: question.options.map(
                      (option) =>
                        new BoxQuestionnaireQuestionOption({
                          id: option.id,
                          imageAlt: option.featureFile?.alternativeText,
                          imageUrl: option.featureFile?.url,
                          isAvailable: option.isAvailable,
                          isAutoSelected: option.isAutoSelected,
                          isSelected: option.isSelected,
                          name: option.name,
                          sourceId: option.source?.id,
                          sourceName: option.source?.name,
                          attributes: valueOrDefault(option.attributes, []).map(
                            (attribute) =>
                              new BoxQuestionnaireQuestionOptionAttribute({
                                label: attribute.label,
                                id: attribute.id,
                                value: attribute.value,
                              }),
                          ),
                        }),
                    ),
                  }),
              ),
              recommendedQuestionnaires: response.recommendedQuestionnaires.map(
                (item) =>
                  new PublicBoxRecommendedQuestionnaire({
                    id: item.id,
                    name: item.name,
                    status: item.status,
                  }),
              ),
              status: response.status,
            }),
        ),
      );
  }

  public endById(id: Guid): Observable<void> {
    return this.boxQuestionnaireApi.endById(id);
  }

  private mapContextOperations(data: {
    canBeFilled: boolean;
    canBeViewed: boolean;
  }): PublicBoxQuestionnaireContextOperations {
    return new PublicBoxQuestionnaireContextOperations({
      canBeFilled: data.canBeFilled,
      canBeViewed: data.canBeViewed,
    });
  }
}
